import React, { useEffect, useState } from 'react';
import { Typography, TextField, Button, Avatar, Chip, Select, MenuItem, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab'
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { PinnersData } from '../../interfaces/Pinners';
import MapIcon from '@mui/icons-material/Map';
import SearchIcon from '@mui/icons-material/Search';
import { AppRoutes } from '../../enums/Approutes';
import Loader from '../../assets/gifs/loader.gif'
import { EmptyState, PinnerProIcon } from '../../assets/icons/Index';
import TotPin from '../../assets/images/totPin.png';
import TotPinPro from '../../assets/images/totPinPro.png';
import { PIN_TYPES_OPTIONS } from '../../enums/MarkerOptions';
import { PinnerTabs } from '../../enums/Pinners';
import { useToast } from '../../hooks/useToast';
import ReactSelect, { SingleValue } from 'react-select';
import { PlaceOption } from '../../interfaces/Mapbox';
import mbxGeocoding from '@mapbox/mapbox-sdk/services/geocoding';
import { getConfig } from '../../config/env';

const Pinners = () => {
  const url = getConfig().BASE_URL;
  const geocodingClient = mbxGeocoding({ accessToken: getConfig().MAPBOX_TOKEN! });
  const { t, i18n } = useTranslation();
  const authToken = sessionStorage.getItem('authToken');
  const { pinnersFetchError, pinnersError } = useToast();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchType, setSearchType] = useState<string>(PinnerTabs.PLACE);
  const [tableData, setTableData] = useState<PinnersData[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [placeOptions, setPlaceOptions] = useState<PlaceOption[]>([]);

  const fetchPlaceSuggestions = async (inputValue: string) => {
    if (!inputValue.trim()) return;

    try {
      const response = await geocodingClient
        .forwardGeocode({
          query: inputValue,
          autocomplete: true,
          limit: 5,
          language: [i18n.language]
        })
        .send();

      const fetchedOptions = response.body.features.map((feature) => ({
        label: feature.place_name,
        value: feature.place_name,
        center: feature.center as [number, number],
      }));

      setPlaceOptions(fetchedOptions);
    } catch (err) {
      console.error('Errore fetchPlaceSuggestions:', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {

      let person = undefined;
      let place = undefined;

      if (searchType === PinnerTabs.PINNER) {
        person = searchQuery.trim();
      } else if (searchType === PinnerTabs.PLACE) {
        if (searchQuery.trim()) {
          place = searchQuery.trim(); // Utilizzo direttamente il valore selezionato
        }
      }

      const params = {
        limit: 10,
        offset: 0,
        person: person,
        place: place,
        language: [i18n.language],
      };

      const response = await axios.get(`${url}/users/`, { params, headers: { 'Authorization': `Bearer ${authToken}` } });
      const { results, count, next } = response.data;
      setTableData(results);
      setCount(count);
      setNext(next);
    } catch (error) {
      console.error('Errore nel fetch dei dati:', error);
      pinnersFetchError();
      setTableData([]);
      setCount(0);
      setNext(null);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };


  const fetchMorePinners = async () => {
    if (!next) return;
    setLoadingMore(true);

    try {
      const response = await axios.get(next);
      const { results, count, next: newNext } = response.data;
      setTableData((prev) => [...prev, ...results]);
      setCount(count);
      setNext(newNext);
    } catch (error) {
      console.error('Errore nel fetch dei dati (load more):', error);
      setLoadingMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearchSubmit = () => {
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleOpenMap = (userId: string) => {
    const user = tableData.find(u => u.id === userId);
    if (user && user.is_pinner_pro) {
      const urlPinner = AppRoutes.PINNER_PRO_PROFILE.replace(':userId', userId);
      window.open(urlPinner, '_blank');
    } else if (userId) {
      const url = AppRoutes.PROFILE.replace(':userId', userId);
      window.open(url, '_blank');
    } else {
      console.error('User ID not found');
      pinnersError();
    }
  };

  if (loading) {
    return <div className='w-screen h-screen flex justify-center items-center'>
      <img src={Loader} alt='' className='w-16 h-auto object-cover' />
    </div>;
  }

  return (
    <div className='w-full h-auto flex flex-col justify-center items-center p-8'>
      {/* Header */}
      <div className='flex flex-col items-center mt-4 mb-2 px-4 sm:mt-6 mb-4 text-center'>
        <p className='text-primary font-bold text-sm sm:text-base'>
          Pinners
        </p>
        <Typography
          variant='h3'
          className='px-0 text-text-primary font-medium md:px-4 mt-2'
          sx={{
            fontSize: { xs: '1.3rem', sm: '2rem' }
          }}
        >
          {t('pinners:title')} <br /> {t('pinners:subTitle')}
        </Typography>
      </div>

             {/* Search bar */}
       <div className="w-full mt-4 mb-8">
         <div className="w-full flex flex-col items-center justify-center">
           {/* Container per select e input - sempre orizzontale */}
           <div className="w-full flex items-center justify-center space-x-3">
           <Select
             value={searchType}
             onChange={(e) => setSearchType(e.target.value)}
             variant="outlined"
             className="w-1/3 md:w-[95px]"
             sx={{
               '& .MuiOutlinedInput-notchedOutline': {
                 borderColor: '#E0E0E0',
               },
               '&:hover .MuiOutlinedInput-notchedOutline': {
                 borderColor: '#E0E0E0',
               },
               '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                 borderColor: '#E0E0E0',
               },
               '& .MuiSelect-select': {
                 color: '#000000',
                 borderRadius: '8px'
               },
             }}
           >
             <MenuItem value={PinnerTabs.PLACE}>{t('pinners:menuPlace')}</MenuItem>
             <MenuItem value={PinnerTabs.PINNER}>{t('pinners:menuPinner')}</MenuItem>
           </Select>
           {searchType === PinnerTabs.PLACE ? (
             <div className="w-full md:w-[30%]">
               <div className="w-full flex space-x-2">
                 <ReactSelect<PlaceOption>
                   placeholder={t('pinners:searchPlace')}
                   inputId="place-select"
                   isClearable
                   options={placeOptions}
                   value={placeOptions.find(option => option.value === searchQuery) || null}
                   onInputChange={(inputValue) => {
                     if (inputValue) {
                       fetchPlaceSuggestions(inputValue);
                     }
                   }}
                   onChange={(selectedOption: SingleValue<PlaceOption>) => {
                     if (selectedOption) {
                       setSearchQuery(selectedOption.value);
                     } else {
                       setSearchQuery('');
                     }
                   }}
                   className='flex-1'
                   styles={{
                     menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                     option: (provided, state) => ({
                       ...provided,
                       backgroundColor: state.isSelected
                         ? '#E0E0E0'
                         : state.isFocused
                           ? '#E0E0E0'
                           : provided.backgroundColor,
                       color: state.isSelected
                         ? 'black'
                         : state.isFocused
                           ? 'black'
                           : provided.color,
                     }),
                     control: (provided, state) => ({
                       ...provided,
                       borderColor: state.isFocused ? '#E0E0E0' : provided.borderColor,
                       boxShadow: state.isFocused ? '0 0 0 1px #E0E0E0' : provided.boxShadow,
                       '&:hover': {
                         borderColor: state.isFocused ? '#E0E0E0' : provided.borderColor,
                       },
                       height: '3.5rem',
                       borderRadius: isDesktop ? '8px 0 0 8px' : '8px',
                     }),
                     singleValue: (provided) => ({
                       ...provided,
                       color: 'black',
                     }),
                   }}
                   menuPortalTarget={document.body}
                 />
                 {/* IconButton solo su desktop */}
                 <IconButton
                   onClick={handleSearchSubmit}
                   className="hidden md:flex"
                   sx={{
                     backgroundColor: '#E9495A',
                     color: 'white',
                     height: '3.5rem',
                     width: '3.5rem',
                     borderRadius: '0 8px 8px 0',
                     '&:hover': {
                       backgroundColor: '#E6263B',
                     },
                   }}
                   style={{ marginLeft: 0 }}
                 >
                   <SearchIcon />
                 </IconButton>
               </div>
             </div>
           ) : (
             <div className="w-full md:w-[30%]">
               <div className="w-full flex space-x-2">
                 <TextField
                   className='flex-1'
                   label={t('pinners:searchPinner')}
                   variant="outlined"
                   value={searchQuery}
                   onChange={handleSearchChange}
                   sx={{
                     '& .MuiOutlinedInput-root': {
                       '& fieldset': {
                         borderColor: '#E0E0E0',
                         borderRadius: isDesktop ? '8px 0 0 8px' : '8px',
                       },
                       '&:hover fieldset': {
                         borderColor: '#E0E0E0',
                       },
                       '&.Mui-focused fieldset': {
                         borderColor: '#E0E0E0',
                       },
                     },
                     '& .MuiInputLabel-root': {
                       color: '#000000',
                     },
                     '& .MuiInputLabel-root.Mui-focused': {
                       color: '#000000',
                     },
                     '& .MuiInputBase-input.Mui-disabled': {
                       color: '#000000',
                     },
                   }}
                 />
                 {/* IconButton solo su desktop */}
                 <IconButton
                   onClick={handleSearchSubmit}
                   className="hidden md:flex"
                   sx={{
                     backgroundColor: '#E9495A',
                     color: 'white',
                     height: '3.5rem',
                     width: '3.5rem',
                     borderRadius: '0 8px 8px 0',
                     '&:hover': {
                       backgroundColor: '#E6263B',
                     },
                   }}
                   style={{ marginLeft: 0 }}
                 >
                   <SearchIcon />
                 </IconButton>
               </div>
             </div>
           )}
           </div>
           
           {/* Pulsante di ricerca su mobile - posizionato sotto select e input */}
           <Button
             onClick={handleSearchSubmit}
             variant="contained"
             className="w-full mt-2 md:hidden"
             sx={{
               backgroundColor: '#E9495A',
               color: 'white',
               height: '2.5rem',
               borderRadius: '8px',
               '&:hover': {
                 backgroundColor: '#E6263B',
               },
             }}
           >
             {t('common:search')}
           </Button>
         </div>

        {/* Helper text place */}
        {searchType === PinnerTabs.PLACE && searchQuery.trim() && (
          <Typography
            variant='body2'
            className='text-center mt-4 text-text-primary md:text-left md:px-16'
          >
            {t('pinners:helperTextStart')} <strong>{searchQuery.trim()}</strong> {t('pinners:helperTextEnd')}
          </Typography>
        )}
      </div>

      {/* Table */}
      <div className="w-full sm:px-4 md:px-16">
        {loading ? (
          <div className='w-full h-auto flex items-center justify-center m-auto'>
            <img src={Loader} alt='Loading' style={{ width: '5rem', height: 'auto', objectFit: 'cover' }} />
          </div>
        ) : !loading && tableData.length > 0 ? (
          <>
            {tableData.map((user) => (
              <div
                key={user.id}
                className="w-full h-auto border-b flex items-center py-4"
                style={{ borderColor: '#E0E0E0' }}
              >
                {/* Sezione sinistra comune (avatar + testo) */}
                <div className="flex justify-start items-center w-full">
                  <div className="relative">
                    <Avatar
                      src={user.profile_picture || 'placeholder.svg'}
                      alt={`${user.first_name} ${user.last_name}`}
                      sx={{
                        width: { xs: '3.5rem', sm: '4rem' },
                        height: { xs: '3.5rem', sm: '4rem' },
                        marginRight: '1.2rem'
                      }}
                    />
                    {user.is_pinner_pro && (
                      <div style={{ position: 'absolute', bottom: -2, right: 15, zIndex: 10 }}>
                        <PinnerProIcon className='' width={23} height={23} />
                      </div>
                    )}
                  </div>
                  <div className="w-3/5 flex flex-col items-start justify-center">
                    <span className="text-text-primary font-semibold text-sm sm:text-base">
                      {user.project_name && user.project_name !== "" 
                        ? user.project_name 
                        : `${user.first_name} ${user.last_name}`}
                    </span>
                    <div className="flex items-center mt-1">
                      <Typography variant="body2" className="flex items-center mr-2">
                        <img src={TotPin} alt='totPins' className="w-8 h-8" />
                        {user.tot_pins}
                      </Typography>
                      <Typography variant="body2" className="flex items-center">
                        <img src={TotPinPro} alt='totPinsPro' className="w-8 h-8" />
                        {user.tot_pins_pro}
                      </Typography>
                    </div>
                  </div>
                </div>

                {/* Chips */}
                <div className="hidden mt-2 md:flex md:w-full md:mt-0 md:ml-4 flex space-x-2 md:justify-center md:justify-start md:items-center">
                  {user.top_trip_types?.map((typeNum, index) => {
                    const option = PIN_TYPES_OPTIONS.find(opt => opt.value === typeNum);
                    if (!option) return null;

                    return (
                      <Chip
                        key={index}
                        label={
                          <span>
                            {option.icon} {t(option.labelKey)}
                          </span>
                        }
                      />
                    );
                  })}
                </div>

                {/* Pulsante a destra (vedi mappa) */}
                <div className="mt-2 md:mt-0 md:ml-4">
                  <Button
                    variant="contained"
                    onClick={() => handleOpenMap(user.id)}
                    sx={{
                      padding: '0.3rem',
                      backgroundColor: '#E9495A',
                      '&:hover': {
                        backgroundColor: '#E6263B',
                      },
                    }}
                  >
                    <MapIcon className="w-6 h-6" />
                  </Button>
                </div>
              </div>
            ))}

            {/* carica altri */}
            {next && (
              <div className="w-full flex justify-center items-center mt-8 mb-4">
                <LoadingButton
                  loading={loadingMore}
                  onClick={fetchMorePinners}
                  sx={{
                    width: { xs: '45%', sm: '15%' },
                    border: '1px solid #E0E0E0',
                    color: '#606060',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#606060',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  {t('pinners:loadMore')}
                </LoadingButton>
              </div>
            )}
          </>
        ) : !loading && tableData.length === 0 ? (
          <div className='w-full h-[40vh] my-4 flex flex-col justify-center items-center gap-4'>
            <Typography variant="body1" className="text-text-primary">
              {t('pinners:noResults')}
            </Typography>
            <EmptyState className='' />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Pinners;
